import { plannedToAnchorDate } from '@/shared/lib/datetime/planned-date';
import type { BoardGroup } from '@/features/boards/public';
import { groupOf, type GanttGroupLabels } from '../adapters';
import { resolveTimelineBarColor } from './timeline-bar-color';
import type {
  TaskSummaryDto,
  GanttGroupBy,
  GroupByOption,
  ProjectStatus,
  TimelineGroupSection,
} from '../types';

export type TimelineGroupBy = Exclude<GanttGroupBy, 'none' | 'assignee'>;

/** Fixed severity order for the priority axis — matches `PRIORITY_GROUPS` in `TaskListView.tsx`. */
const PRIORITY_SECTION_ORDER = ['urgent', 'high', 'medium', 'low', 'none'];

/** `'assignee'`/`'none'` both mean the swimlane-by-person layout in Timeline — only the other three activate the grouped-sections layout. */
export function toTimelineGroupBy(groupBy: GroupByOption): TimelineGroupBy | null {
  return groupBy === 'status' || groupBy === 'priority' || groupBy === 'group' ? groupBy : null;
}

/** Canonical section order per axis — board status order, fixed priority severity, or board-group alphabetical order (same criteria as `resolveGroupDefs` in `TaskListView.tsx`). */
export function resolveSectionOrder(
  groupBy: TimelineGroupBy,
  statuses: ProjectStatus[],
  boardGroups: BoardGroup[],
): string[] {
  if (groupBy === 'status') {
    return [...statuses].sort((a, b) => a.order - b.order).map((s) => s.id);
  }
  if (groupBy === 'priority') {
    return PRIORITY_SECTION_ORDER;
  }
  return [...boardGroups].sort((a, b) => a.name.localeCompare(b.name)).map((g) => g.id);
}

/**
 * Groups tasks into read-only sections (status/priority/group), one row per task.
 * `sectionOrder` fixes the canonical key order for the active axis (board status
 * order, fixed priority severity, or board-group alphabetical order — see
 * `TaskListView.tsx`'s `resolveGroupDefs`); keys not present in it are appended
 * at the end in first-appearance order. Sections with no tasks are never produced.
 */
export function buildGroupedTimelineRows(
  tasks: TaskSummaryDto[],
  groupBy: TimelineGroupBy,
  labels: GanttGroupLabels,
  sectionOrder: string[],
): TimelineGroupSection[] {
  const sections = new Map<string, TimelineGroupSection>();
  const extraOrder: string[] = [];

  for (const task of tasks) {
    if (!task.startDate || !task.dueDate) continue;

    const g = groupOf(task, groupBy, labels);
    let section = sections.get(g.key);
    if (!section) {
      section = { key: g.key, label: g.label, color: g.color, count: 0, rows: [] };
      sections.set(g.key, section);
      if (!sectionOrder.includes(g.key)) extraOrder.push(g.key);
    }

    section.rows.push({
      id: task.id,
      title: task.title,
      startDate: plannedToAnchorDate(task.startDate),
      endDate: plannedToAnchorDate(task.dueDate),
      color: resolveTimelineBarColor(task.assignee?.id),
      isCompleted: task.isCompleted,
      assignee: task.assignee
        ? {
            id: task.assignee.id,
            name: task.assignee.displayName ?? '—',
            avatarUrl: task.assignee.avatarUrl ?? undefined,
          }
        : null,
    });
    section.count = section.rows.length;
  }

  return [...sectionOrder, ...extraOrder]
    .map((key) => sections.get(key))
    .filter((section): section is TimelineGroupSection => !!section);
}
